'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import styles from './subscription-purchase-content.module.scss';
import { useCommonData } from '@udemy-v2/common-data';
import { useFormatNumber, useI18n } from '@udemy-v2/i18n';
import { useClpNoticeContext } from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import { aggregateCollectionStats, FALLBACK_COLLECTION_STATS, PLAN_SELECTION_CHECKOUT_URL, PlanPeriodStyle, SubscribeFromPageCookie, SubscriptionCTA, SubscriptionCTAClickEvent, SubscriptionLearnMoreEvent, SubscriptionOfferPresented, usePersonalPlanCollectionStats } from '@udemy-v2/react-subscription-browse';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { useDeviceType } from '@udemy-v2/hooks';
const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];
export const SubscriptionPurchaseContent = () => {
  const $ = _c(52);
  const {
    gettext,
    interpolate
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const commonData = useCommonData();
  const {
    isLoading,
    unwrappedConsumerSubscriptionPlan,
    subscriptionCTAConfiguration,
    subscriptionTiersEnabled
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption
  } = useClpSelectedOption();
  const {
    isCourseInValuePlanCatalog,
    unwrappedPersonalPlan,
    subscribedProductType
  } = useClpSubscriptionContext();
  const isGwgStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
  const clpNoticeContext = useClpNoticeContext();
  const course = useClpCourse();
  const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
  let t0;
  if ($[0] !== gettext) {
    t0 = gettext("Subscribe to Udemy\u2019s top courses");
    $[0] = gettext;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const defaultTitle = t0;
  const title = subscriptionPriceBoxNotice?.title ?? defaultTitle;
  const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
  const subscriptionPlanStats = contentCollections ? aggregateCollectionStats(contentCollections.map(_temp)) : FALLBACK_COLLECTION_STATS;
  const personalPlanStats = usePersonalPlanCollectionStats();
  const personalPlanCourseCount = personalPlanStats && "numberOfCourses" in personalPlanStats ? personalPlanStats.numberOfCourses : isGwgStandaloneSubscriber && contentCollections ? subscriptionPlanStats.numberOfCourses : PERSONAL_PLAN_COURSE_COUNT;
  const tiersCourseCount = personalPlanCourseCount;
  const defaultSubtitle = subscriptionTiersEnabled && isCourseInValuePlanCatalog ? interpolate(gettext("Get this premium course, plus %(courses_count)s+ of our top-rated courses with Personal Plan."), {
    courses_count: formatNumber(tiersCourseCount)
  }, true) : subscriptionTiersEnabled ? interpolate(gettext("Get this premium course, plus %(courses_count)s+ of our top-rated courses with Personal Plan."), {
    courses_count: formatNumber(tiersCourseCount)
  }, true) : interpolate(gettext("Get this course, plus %(courses_count)s+ of our top-rated courses, with Personal Plan."), {
    courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)
  }, true);
  const description = subscriptionPriceBoxNotice?.subtitle ?? defaultSubtitle;
  let t1;
  if ($[2] !== gettext || $[3] !== subscriptionPriceBoxNotice?.subtitle_link_text) {
    t1 = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext("Learn more");
    $[2] = gettext;
    $[3] = subscriptionPriceBoxNotice?.subtitle_link_text;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const learnMoreText = t1;
  const subscriptionContextLearnMoreUrl = unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? "https://udemy.com/personal-plan/";
  const learnMoreUrl = subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
  const learnMoreTarget = subscriptionPriceBoxNotice?.subtitle_link_target === "new_tab" ? "_blank" : "_self";
  let t2;
  if ($[5] !== course.id || $[6] !== learnMoreUrl || $[7] !== unwrappedConsumerSubscriptionPlan) {
    t2 = () => {
      if (SUBSCRIPTION_ATTRIBUTION_PATHS.some(path => learnMoreUrl.includes(path))) {
        SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      }
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionLearnMoreEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          uiRegion: UI_REGION,
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[5] = course.id;
    $[6] = learnMoreUrl;
    $[7] = unwrappedConsumerSubscriptionPlan;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleLearnMoreClick = t2;
  let t3;
  if ($[9] !== course.id || $[10] !== unwrappedConsumerSubscriptionPlan) {
    t3 = () => {
      SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionCTAClickEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? "trial" : "paid",
          uiRegion: UI_REGION,
          courseId: parseInt(course.id, 10),
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[9] = course.id;
    $[10] = unwrappedConsumerSubscriptionPlan;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleCtaClick = t3;
  const showPPPrice = subscriptionTiersEnabled && isCourseInValuePlanCatalog && unwrappedPersonalPlan;
  let t4;
  if ($[12] !== commonData?.user?.country || $[13] !== course.frontendTrackingId || $[14] !== course.id || $[15] !== unwrappedConsumerSubscriptionPlan) {
    t4 = unwrappedConsumerSubscriptionPlan ? () => {
      publishEvent(new SubscriptionOfferPresented({
        subscriptionPlan: unwrappedConsumerSubscriptionPlan,
        productType: unwrappedConsumerSubscriptionPlan.productType,
        countryCode: commonData?.user?.country ?? "",
        courseId: parseInt(course.id, 10),
        courseTrackingId: course.frontendTrackingId ?? null,
        uiRegion: UI_REGION
      }));
    } : undefined;
    $[12] = commonData?.user?.country;
    $[13] = course.frontendTrackingId;
    $[14] = course.id;
    $[15] = unwrappedConsumerSubscriptionPlan;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handleOfferImpression = t4;
  if (!unwrappedConsumerSubscriptionPlan) {
    return null;
  }
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classNames(styles.title, "ud-heading-serif-xxl");
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== title) {
    t6 = <div className={t5}>{title}</div>;
    $[18] = title;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = classNames(styles.description, "ud-text-sm");
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== handleLearnMoreClick || $[22] !== learnMoreTarget || $[23] !== learnMoreText || $[24] !== learnMoreUrl) {
    t8 = <>{" "}<a className={styles["learn-more-link"]} data-purpose="subscription-learn-more-link" href={learnMoreUrl} target={learnMoreTarget} onClick={handleLearnMoreClick}>{learnMoreText}</a></>;
    $[21] = handleLearnMoreClick;
    $[22] = learnMoreTarget;
    $[23] = learnMoreText;
    $[24] = learnMoreUrl;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== description || $[27] !== t8) {
    t9 = <div className={t7}>{description}{t8}</div>;
    $[26] = description;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t6 || $[30] !== t9) {
    t10 = <div>{t6}{t9}</div>;
    $[29] = t6;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const t11 = CLPPurchaseOption.SUBSCRIPTION === selectedPurchaseOption ? "primary" : "secondary";
  const t12 = parseInt(course.id, 10);
  const t13 = showPPPrice ? unwrappedPersonalPlan : unwrappedConsumerSubscriptionPlan;
  let t14;
  if ($[32] !== showPPPrice || $[33] !== subscriptionCTAConfiguration || $[34] !== subscriptionPriceBoxNotice || $[35] !== unwrappedConsumerSubscriptionPlan) {
    t14 = subscriptionCTAConfiguration?.upgradeUrl ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: subscriptionCTAConfiguration.upgradeUrl
    } : showPPPrice ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: `${PLAN_SELECTION_CHECKOUT_URL}?product_type=${unwrappedConsumerSubscriptionPlan.productType}`
    } : subscriptionPriceBoxNotice;
    $[32] = showPPPrice;
    $[33] = subscriptionCTAConfiguration;
    $[34] = subscriptionPriceBoxNotice;
    $[35] = unwrappedConsumerSubscriptionPlan;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = subscriptionCTAConfiguration?.directCheckout;
  const t16 = subscriptionCTAConfiguration?.selectPlanText;
  const t17 = subscriptionCTAConfiguration?.purchaseCtaText;
  const t18 = isMobile ? PlanPeriodStyle.MULTILINE_NO_MARGIN : PlanPeriodStyle.MULTILINE_MARGIN;
  let t19;
  if ($[37] !== handleCtaClick || $[38] !== handleOfferImpression || $[39] !== isLoading || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== t15 || $[45] !== t16 || $[46] !== t17 || $[47] !== t18) {
    t19 = <SubscriptionCTA className={styles["subscription-cta"]} uiRegion={UI_REGION} subtitleClassName={styles["cta-subtitle"]} data-purpose="subscription-purchase-button" offerCtaStyle={t11} onCTAClick={handleCtaClick} courseId={t12} includeTrialDaysOnCta={false} plan={t13} trackImpression={handleOfferImpression} noticeData={t14} noticeDataLoading={isLoading} directCheckout={t15} selectPlanText={t16} purchaseCtaText={t17} planPeriodStyle={t18} />;
    $[37] = handleCtaClick;
    $[38] = handleOfferImpression;
    $[39] = isLoading;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t10 || $[50] !== t19) {
    t20 = <div data-purpose="subscription-purchase-section">{t10}{t19}</div>;
    $[49] = t10;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
};
function _temp(contentCollection) {
  return contentCollection.stats;
}